/**
 * Central dispatcher for interactive generative-UI actions.
 *
 * Every canvas primitive (SuggestionBar, InteractiveTaskCard, TaskKanban,
 * DayDashboard, CalendarWidget, and the button/chip/link primitives) reports
 * clicks through a single `onAction({ type, ... })` callback. Historically the
 * surfaces only handled `query` and `focus_input`, so every task checkbox,
 * suggestion chip, kanban drag, and dashboard button was silently dropped.
 *
 * This maps each emitted action type to a concrete effect. Task mutations have
 * no REST endpoint — they are performed by the chat tool layer (complete_task /
 * update_task / delete_task / calendar_*), so we route them back through the
 * conversation as concise instructions the model turns into the right tool call
 * (the LLM-native architecture described in SALES_AI_ARCHITECTURE.md).
 *
 * @param {object} action - the emitted action ({ type, query?, task_id?, ... })
 * @param {object} handlers
 * @param {function} handlers.sendMessage - (text) => Promise, sends a chat turn
 * @param {function} [handlers.prefillInput] - (value) => void, fills + focuses the composer
 * @returns {Promise|void} the sendMessage promise when a turn is sent
 */
export function dispatchComponentAction(action, { sendMessage, prefillInput } = {}) {
  if (!action || !action.type || typeof sendMessage !== 'function') return;

  // Human-readable reference to a task: prefer the title, fall back to id.
  const taskRef = (a) =>
    a.task_title ? `"${a.task_title}"` : a.task_id ? `(id ${a.task_id})` : 'that task';

  switch (action.type) {
    // ── Direct query passthrough (suggestions, chips, links, buttons) ──────
    case 'query':
    case 'suggestion':
    case 'chip':
    case 'link':
    case 'button':
    case 'quick-action':
      if (action.query) return sendMessage(action.query);
      if (action.text) return sendMessage(action.text);
      return;

    // ── Composer prefill ───────────────────────────────────────────────────
    case 'focus_input':
      prefillInput?.(action.defaultValue || '');
      return;

    // ── Task mutations (routed through chat tools) ─────────────────────────
    case 'complete_task':
      return sendMessage(`Mark the task ${taskRef(action)} as complete.`);

    case 'delete_task':
      return sendMessage(`Delete the task ${taskRef(action)}.`);

    case 'move_task':
      return sendMessage(
        `Update the task ${taskRef(action)} status to "${action.to_status}".`
      );

    case 'edit_task':
      prefillInput?.(`Update the task ${taskRef(action)}: `);
      return;

    case 'schedule_task':
      return sendMessage(
        `Help me schedule the task ${taskRef(action)}. Check my calendar for a free slot and propose a time.`
      );

    case 'add_task':
      prefillInput?.('Add a task: ');
      return;

    // ── Navigation / informational ─────────────────────────────────────────
    case 'filter_tasks': {
      const filter = action.filter;
      if (filter === 'intel') return sendMessage('Show me my latest intelligence items.');
      const label = filter ? filter.replace(/_/g, ' ') : '';
      return sendMessage(`Show my ${label} tasks.`.replace(/\s+/g, ' ').trim());
    }

    case 'select_task':
    case 'view_task':
      return sendMessage(`Show me the details for task ${taskRef(action)}.`);

    case 'view_intel':
      if (action.query) return sendMessage(action.query);
      return sendMessage(
        action.intel?.title
          ? `Tell me more about: ${action.intel.title}`
          : 'Show me more about that intelligence item.'
      );

    case 'view_event':
      if (action.query) return sendMessage(action.query);
      return sendMessage(
        action.event?.title
          ? `Tell me about my "${action.event.title}" event.`
          : 'Show me the details for that calendar event.'
      );

    case 'view_all':
      return sendMessage(action.query || 'Show me everything on my plate today.');

    case 'schedule':
      if (action.query) return sendMessage(action.query);
      return sendMessage('Help me schedule that on my calendar.');

    default:
      // Last resort: honor an attached query, otherwise log for triage.
      if (action.query) return sendMessage(action.query);
      // eslint-disable-next-line no-console
      console.warn('[dispatchComponentAction] unhandled action type:', action.type, action);
      return;
  }
}

export default dispatchComponentAction;
